/*
 * This service takes care of dynamic routing stuff for the charge repay driver application
 * It holds current states of the application and driver to determine the correct flow for different situations
 */
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AuthHandler } from '@cr-driver/handler/auth.handler'
import { ChargeRepayErrorCodes } from '@cr-driver/models.errors'
import { IChargeRepayError, IConfigCode } from '@cr-driver/models.interfaces'
import { DriverStore } from '@cr-driver/stores/driver.store'
import { IamStore } from '@cr-driver/stores/iam.store'
import { environment } from '@cr-environments/environment'
import { BehaviorSubject, catchError, combineLatest, map, take, tap, throwError } from 'rxjs'
import { filter } from 'rxjs/operators'

/**
 * These are the state a driver can have
 * The application flow bases on them!
 * Sometimes they are positive sometimes negative todo: this could be changed to all positive or all negative
 */
export enum DriverStates {
  HAS_MULTIPLE_ORGANIZATIONS = 'HAS_MULTIPLE_ORGANIZATIONS',
  HAS_SINGLE_ORGANIZATION = 'HAS_SINGLE_ORGANIZATION',
  HAS_CHARGE_POINT = 'HAS_CHARGE_POINT',
  WALLBOX_NEVER_CONNECTED = 'WALLBOX_NEVER_CONNECTED',
  HAS_NO_MILEAGES = 'HAS_NO_MILEAGES',
  HAS_NO_ACCOUNT_DETAILS = 'HAS_NO_ACCOUNT_DETAILS',
  HAS_NO_ENERGY_CONTRACT = 'HAS_NO_ENERGY_CONTRACT',
  IS_DIRECT_OCPP = 'IS_DIRECT_OCPP',
  REIMBURSEMENT_ACTIVE = 'REIMBURSEMENT_ACTIVE',
  ACTIONS_NEEDED = 'ACTIONS_NEEDED',
  MILEAGES_OUTDATED = 'MILEAGES_OUTDATED',
}

/**
 * The state handler checks data coming from the store and creates a state array
 * This array contains properties depending on the store data
 * Once the state array is created the state handler decides where to route the user next
 *
 * This could be the onboarding, the onboarding-register, the order-state overview, the charges or manual page
 * It is important to not change the logic in here mindlessly, since it can break anything
 *
 * In addition, the state handler provides routing methods shadowing the "router.navigate" function for pure convenience
 */
@Injectable({
  providedIn: 'root',
})
export class StateHandler {
  private origin: string
  private iamStore = inject(IamStore)
  private driverStore = inject(DriverStore)
  private authHandler = inject(AuthHandler)
  private router = inject(Router)

  private stateSubject$ = new BehaviorSubject<DriverStates[]>([])
  public readonly state$ = this.stateSubject$.asObservable()
  /**
   * Sets the initial URL the page was opened but only stores it once!
   * This is needed to know where the user was intended to go and route them there as soon as everything is initialized
   */
  setOrigin(_origin: string) {
    if (!this.origin) {
      this.origin = _origin
    }
  }

  /**
   *    Determines the initial state in order to route and control app behavior
   *    At this point all logical errors are handled. E.g: it is not possible to have an empty organizations array coming,
   *    since the store would have thrown an error beforehand.
   *    @returns DriverStates[]
   */
  determineState() {
    return combineLatest([
      this.driverStore.chargePoint$,
      this.iamStore.crOrganizations$,
      this.driverStore.car$,
      this.driverStore.accountDetails$,
      this.driverStore.chargePointEverConnected$,
      this.driverStore.princeInformation$,
      this.iamStore.activeCROrganization$,
    ]).pipe(
      take(1),
      map(([chargePoint, crOrganizations, car, accDetails, everConnected, priceInfo, activeOrg]) => {
        const _state: DriverStates[] = []

        if (crOrganizations.length > 1) {
          _state.push(DriverStates.HAS_MULTIPLE_ORGANIZATIONS)
        } else {
          _state.push(DriverStates.HAS_SINGLE_ORGANIZATION)
        }

        if (chargePoint) {
          _state.push(DriverStates.HAS_CHARGE_POINT)

          if (chargePoint.direct_ocpp) {
            _state.push(DriverStates.IS_DIRECT_OCPP)
          }

          if (!car || car.mileages.length < 1) {
            _state.push(DriverStates.HAS_NO_MILEAGES)
          }

          if (!car || car.car.mileage_state !== 'mileage_ok') {
            _state.push(DriverStates.MILEAGES_OUTDATED)
          }

          if (!everConnected) {
            _state.push(DriverStates.WALLBOX_NEVER_CONNECTED)
          }

          if (!priceInfo?.latest_price?.price) {
            _state.push(DriverStates.HAS_NO_ENERGY_CONTRACT)
          }

          if (activeOrg && activeOrg.reimbursement_activated) {
            _state.push(DriverStates.REIMBURSEMENT_ACTIVE)

            if (!accDetails) {
              _state.push(DriverStates.HAS_NO_ACCOUNT_DETAILS)
            }
          }

          if (
            _state.includes(DriverStates.HAS_NO_MILEAGES) ||
            _state.includes(DriverStates.HAS_NO_ENERGY_CONTRACT) ||
            _state.includes(DriverStates.HAS_NO_ACCOUNT_DETAILS)
          ) {
            _state.push(DriverStates.ACTIONS_NEEDED)
          }
        }

        return _state
      }),
      tap((state) => {
        this.stateSubject$.next(state)
      }),
      catchError(() => {
        const error: IChargeRepayError = {
          severity: 'panic',
          errorCode: ChargeRepayErrorCodes.DETERMINING_STATE_ERROR,
        }
        return throwError(() => error)
      })
    )
  }

  calculateState() {
    /* Check if the driver has a charge point and an organisation, this is the most common case.
     * If both is true the driver is ready for operation and can be forwarded to the charges view.
     */
    this.stateSubject$.pipe(take(1)).subscribe((_state) => {
      if (_state.includes(DriverStates.HAS_CHARGE_POINT)) {
        // Check if there is an origin first and route the user to their target
        this.calculateChargePointState()
      } else {
        void this.router.navigate(['onboarding'])
      }
    })
  }

  calculateChargePointState() {
    // for all wallbox types: check that the user added all necessary properties
    // if not forward to onboarding register
    this.stateSubject$.pipe(take(1)).subscribe((_state) => {
      if (_state.includes(DriverStates.ACTIONS_NEEDED)) {
        void this.router.navigate(['onboarding'])
        return
      }

      // check if direct ocpp
      if (_state.includes(DriverStates.IS_DIRECT_OCPP)) {
        // check if the con_connected_ts flag exists in order to determine if the wallbox was ever connected
        // if not forward to manual
        if (_state.includes(DriverStates.WALLBOX_NEVER_CONNECTED)) {
          this.goToManual()
          return
        }
      }

      // If the onboarding/register is done but the drivers mileages are outdated forward to mileages
      if (_state.includes(DriverStates.MILEAGES_OUTDATED)) {
        this.goToMileage()
        return
      }

      // All requirements were met, check if there was an origin given and forward to origin, if not /charges is default
      if (this.hadOrigin()) {
        void this.router.navigate([this.origin])
      } else {
        this.goToCharges()
      }
    })
  }

  private hadOrigin() {
    return this.origin && this.origin !== '/' && this.origin !== '/shell' && this.origin !== '/onboarding/register' && !this.origin.includes('login?')
  }

  goToCharges() {
    void this.router.navigate(['/driver/charges'])
  }

  goToMileage() {
    void this.router.navigate(['/driver/mileage'])
  }

  goToMyWallbox() {
    void this.router.navigate(['/driver/wallbox'])
  }

  goToAccounting() {
    void this.router.navigate(['/driver/accounting'])
  }

  logout() {
    // No need for routing here, the AuthGuard takes care
    this.authHandler.logout()
  }

  goToSettings() {
    void this.router.navigate(['/driver/settings'])
  }

  goToGatewaySettings() {
    void this.router.navigate(['/driver/settings/gateway'])
  }

  goToAccountSettings() {
    void this.router.navigate(['/driver/settings/accounting'])
  }

  goToContractSettings() {
    void this.router.navigate(['/driver/settings/contract'])
  }

  goToRFIDSettings() {
    void this.router.navigate(['/driver/settings/rfid'])
  }

  goToBackendProviderSettings() {
    void this.router.navigate(['/driver/settings/backend-provider'])
  }

  goToAutoChargeAssignSettings() {
    void this.router.navigate(['/driver/settings/auto-charge-assign'])
  }

  goToManual() {
    this.driverStore.configurationCode$
      .pipe(
        take(1),
        filter((cCode: IConfigCode) => !!cCode)
      )
      .subscribe({
        next: (config) => {
          const id = config.wallbox_id
          void this.router.navigate(['/driver/manual/', id])
        },
      })
  }

  goToLogin() {
    void this.router.navigate(['/login'])
  }

  goToOnboardingPrerequisites() {
    void this.router.navigate(['/onboarding/prerequisites'])
  }

  goToNoWallbox() {
    void this.router.navigate(['/onboarding/no-wallbox'])
  }

  openContactFormLink() {
    const contactURL = environment?.contactLink

    if (contactURL && window) {
      window.open(contactURL, '_blank')
    }
  }

  goToWallboxSelection() {
    void this.router.navigate(['/onboarding/wallbox-selection'])
  }

  openExternalLink(customerLink: string) {
    window.open(customerLink, '_blank')
  }

  goToOnboardingRegister() {
    void this.router.navigate(['/onboarding/register'])
  }

  goToMainPage() {
    void this.router.navigate(['/'])
  }

  goToOrderState() {
    void this.router.navigate(['/onboarding/order'])
  }

  removeMileagesOutdated() {
    this.state$.pipe(take(1)).subscribe((state) => {
      this.stateSubject$.next(state.filter((ds) => ds !== DriverStates.MILEAGES_OUTDATED))
    })
  }
}
